// src/store/slices/TermDepositSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchTermDepositUrl, createTermDepositUrl, fetchTermDepositInterestUrl, createTermDepositInterestUrl, createTermDepositPreMatureInterestUrl, fetchTermDepositInterestPreMatureUrl } from '../../services/masters/termDepositMaster';
import { ACTIONS } from '../../constants/apiEndpoints';

export const fetchTermDeposits = createAsyncThunk(
  ACTIONS.fetch_term_deposits,
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchTermDepositUrl();
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchTermDepositsInterest = createAsyncThunk(
  ACTIONS.fetch_term_deposits_interest,
  async (parentId, { rejectWithValue }) => {
    try {
      const encodedParentId = btoa(parentId.toString()); // Encode the parentId
      const response = await fetchTermDepositInterestUrl(encodedParentId);
      return response.data.data;
    } catch (error) {
      const status = error.response?.status;
      const errorCode = error.response?.data?.errorCode;

      // ✅ Handle NO_RECORDS_FOUND gracefully
      if (status === 404 && errorCode === "NO_RECORDS_FOUND") {
        return [];
      }

      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchTermDepositsInterestPreMatureByParentId = createAsyncThunk(
  ACTIONS.fetch_term_deposits_interest_pre_mature,
  async (parentId, { rejectWithValue }) => {
    try {
      const encodedParentId = btoa(parentId.toString()); // Encode the parentId
      const response = await fetchTermDepositInterestPreMatureUrl(encodedParentId);
      return response.data.data;
    } catch (error) {
      const status = error.response?.status;
      const errorCode = error.response?.data?.errorCode;

      // ✅ Handle NO_RECORDS_FOUND gracefully
      if (status === 404 && errorCode === "NO_RECORDS_FOUND") {
        return [];
      }

      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const TermDepositSlice = createSlice({
  name: 'termDeposits',
  initialState: {
  items: [],
  interestItems: [],
  preMatureItems: [],     // ✅ pre-mature records
  loading: false,         // ✅ normal loading
  error: null,            // ✅ normal error
  interestLoading: false, // ✅ interest loading
  preMatureLoading: false, // ✅ pre-mature loading
  preMatureError: null,    // ✅ pre-mature error
},
  reducers: {
    clearInterestData: (state) => {
      state.interestItems = [];
      state.preMatureItems = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTermDeposits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTermDeposits.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchTermDeposits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchTermDepositsInterest.pending, (state) => {
        state.interestLoading = true;
        state.error = null;
      })
      .addCase(fetchTermDepositsInterest.fulfilled, (state, action) => {
        state.interestLoading = false;
        state.interestItems = action.payload; // ✅ set interest items
      })
      .addCase(fetchTermDepositsInterest.rejected, (state, action) => {
        state.interestLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchTermDepositsInterestPreMatureByParentId.pending, (state) => {
        state.preMatureLoading = true;   // ✅ separate loading
        state.preMatureError = null;     // ✅ separate error
      })
      .addCase(fetchTermDepositsInterestPreMatureByParentId.fulfilled, (state, action) => {
        state.preMatureLoading = false;
        state.preMatureItems = action.payload;   // ✅ separate items
      })
      .addCase(fetchTermDepositsInterestPreMatureByParentId.rejected, (state, action) => {
        state.preMatureLoading = false;
        state.preMatureError = action.payload;   // ✅ separate error
      });

  },
});

export const createTermDeposits = createAsyncThunk(
  ACTIONS.create_term_deposits,
  async (loanTypeObj, { rejectWithValue }) => {
    try {
      const payload = {
        data: JSON.stringify(loanTypeObj), // stringified inner data
        target: 'society.models.master.termDeposit.TermDeposit',
        compressed: false
      };
      const response = await createTermDepositUrl(payload);

      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);


export const createDepositInterest = createAsyncThunk(
  ACTIONS.create_deposit_interest,   // also update your ACTIONS
  async (depositInterestPayload, { rejectWithValue }) => {
    try {

      const payload = {
        data: JSON.stringify(depositInterestPayload), // stringified inner data
        target: 'society.models.master.termDeposit.DepositTypeInterest',
        compressed: false
      };
      const response = await createTermDepositInterestUrl(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createTermDepositPreMatureInterest = createAsyncThunk(
  ACTIONS.create_term_deposit_pre_mature_interest,   // also update your ACTIONS
  async (depositInterestPayload, { rejectWithValue }) => {
    try {

      const payload = {
        data: JSON.stringify(depositInterestPayload), // stringified inner data
        target: 'society.models.master.termDeposit.PreMatureInterest',
        compressed: false
      };
      const response = await createTermDepositPreMatureInterestUrl(payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);





export const { clearInterestData } = TermDepositSlice.actions;

export default TermDepositSlice.reducer;
